import { useState } from "react";
import { useDataProvider, useGetIdentity, useNotify } from "ra-core";
import { useNavigate } from "react-router";
import { Link2, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company } from "../types";
import { asWebsiteUrl, fetchEnrichment, usePastedUrl } from "./CompanyEnrich";

// LDF: In der Firmenliste eine kopierte Website-Adresse mit Cmd+V einfügen →
// Firma wird angelegt (oder die vorhandene geöffnet) und die gefundenen Daten zur Übernahme angeboten.

const hostOf = (url: string) => new URL(url).hostname.replace(/^www\./, "").toLowerCase();

export const useCompanyFromUrl = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider();
  const [busy, setBusy] = useState(false);

  const run = async (url: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const host = hostOf(url);
      const { data: existing } = await getSupabaseClient()
        .from("companies")
        .select("id,name,website")
        .ilike("website", `%${host}%`)
        .limit(5);
      const match = (existing ?? []).find((c) => c.website && hostOf(asWebsiteUrl(c.website) ?? c.website) === host);
      const enrichment = await fetchEnrichment(url);
      if (match) {
        notify(`${match.name} ist schon im CRM – neue Angaben zur Übernahme geöffnet`, { type: "info" });
        navigate(`/companies/${match.id}/show`, { state: { enrichment } });
        return;
      }
      const { data: created } = await dataProvider.create<Company>("companies", {
        data: {
          name: enrichment.legal_name ?? enrichment.name ?? host,
          website: enrichment.website ?? url,
          sales_id: identity?.id,
        } as Partial<Company>,
      });
      notify("Firma angelegt – bitte die gefundenen Angaben prüfen", { type: "success" });
      navigate(`/companies/${created.id}/show`, { state: { enrichment } });
    } catch (e) {
      notify(`Konnte Firma nicht aus der Website anlegen: ${e instanceof Error ? e.message : String(e)}`, {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return { run, busy };
};

export const CompanyFromUrl = () => {
  const { run, busy } = useCompanyFromUrl();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");

  usePastedUrl((url) => run(url));

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} disabled={busy} title="Oder einfach Cmd+V auf dieser Seite">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
        {busy ? "Website wird gelesen …" : "Aus Website anlegen"}
      </Button>
    );
  }

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const url = asWebsiteUrl(value);
        if (url) run(url);
      }}
    >
      <input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPaste={(e) => {
          const url = asWebsiteUrl(e.clipboardData.getData("text/plain"));
          if (url) {
            e.preventDefault();
            setValue(url);
            run(url);
          }
        }}
        placeholder="https://… einfügen"
        className="h-8 w-64 rounded-md border border-input bg-background px-2 text-sm"
      />
      <Button type="submit" size="sm" disabled={busy || !asWebsiteUrl(value)}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Anlegen"}
      </Button>
    </form>
  );
};
